// ─── Section 105 Portal Card (Employer Dashboard) ─────────────────
// Shows on the employer dashboard when the internal team has sent a Section 105
// recommendation OR if the employer hasn't started the wizard yet.
import React, { useState } from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';
import Section105Wizard from '../ibos/Section105Wizard';

export default function Section105PortalCard({ client }) {
  const [showWizard, setShowWizard] = useState(false);
  const s105 = (client?.ibosConfig && client.ibosConfig.section105) || {};
  // Default to ON when undefined — internal team can flip the toggle off
  // from the CRM client drawer to hide the advisor from this employer.
  if (s105.showInPortal === false) return null;
  const presented = !!s105.presentedToEmployer;
  const completed = !!s105.answers && Object.keys(s105.answers).length >= 6;

  // Decide variant
  // 1) presented: HC team sent a recommendation → show "Review your recommendation"
  // 2) completed: employer has filled wizard → show "View your saved plan"
  // 3) neither: invite to start
  let title;
  let subtitle;
  let cta;

  if (presented) {
    title = 'Your Section 105 plan recommendation is ready';
    subtitle = `Your HealthCues team has prepared a tailored plan recommendation based on your company profile. Review it and request your consultation.`;
    cta = 'Review recommendation';
  } else if (completed) {
    title = 'Your Section 105 plan summary';
    subtitle = `You completed the Healthcare Reimbursement Advisor. Review your recommendation or request a consultation.`;
    cta = 'View summary';
  } else {
    title = 'Find your ideal Healthcare Reimbursement plan';
    subtitle = `Take 2 minutes to answer 6 quick questions \u2014 we'll recommend the right plan structure for your company.`;
    cta = 'Start advisor';
  }

  return (
    <>
      <section
        data-testid="s105-portal-card"
        style={{
          background: presented ? 'linear-gradient(135deg, #f0f7ff 0%, #e8f2ff 100%)' : C.card,
          border: `1px solid ${presented ? C.accent : C.border}`,
          borderRadius: 12,
          padding: 20,
          boxShadow: presented ? '0 4px 14px rgba(0, 102, 204, 0.08)' : '0 1px 3px rgba(0,0,0,0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: presented ? C.accent : C.accentBg,
              color: presented ? '#fff' : C.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon name="medical-outline" style={{ fontSize: 24 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: presented ? C.accent : C.textLight,
                marginBottom: 6,
              }}
            >
              {presented ? 'New recommendation' : 'Healthcare Reimbursement Advisor'}
            </div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: C.text, margin: '0 0 6px' }}>{title}</h3>
            <p style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, margin: 0 }}>{subtitle}</p>

            <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setShowWizard(true)}
                data-testid="s105-portal-cta"
                style={{
                  padding: '10px 18px',
                  background: C.accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                {cta}
                <Icon name="arrow-forward" style={{ fontSize: 12, marginLeft: 6, verticalAlign: 'middle' }} />
              </button>
              {presented && s105.recommendedPlanId && (
                <span
                  style={{
                    fontSize: 12,
                    color: C.textMid,
                    fontWeight: 600,
                  }}
                >
                  Recommended: <strong style={{ color: C.text }}>{formatPlanLabel(s105.recommendedPlanId)}</strong>
                </span>
              )}
              {s105.employerConsultationRequested && (
                <span
                  style={{
                    fontSize: 12,
                    color: C.green,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                  }}
                >
                  <Icon name="checkmark-circle" style={{ fontSize: 14 }} />
                  Consultation requested
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {showWizard && (
        <div
          data-testid="s105-portal-modal"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
          onClick={() => setShowWizard(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 1100,
              maxHeight: '92vh',
              background: C.bg,
              borderRadius: 12,
              overflow: 'auto',
              boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
            }}
          >
            <Section105Wizard
              clientId={client?.id || client?._id}
              mode="employer"
              onClose={() => setShowWizard(false)}
            />
          </div>
        </div>
      )}
    </>
  );
}

function formatPlanLabel(planId) {
  const labels = {
    qsehra: 'QSEHRA',
    excepted_benefit: 'Excepted Benefit HRA',
    ichra: 'ICHRA',
    integrated_merp: 'Section 105 MERP',
    gchra: 'Group Coverage HRA',
  };
  return labels[planId] || planId;
}
